<template>
  <div class="profile-panel">
    <el-card class="section-card profile-card" shadow="never">
      <div class="section-head">
        <div>
          <h2>Employee profile</h2>
        </div>
        <el-button type="primary" @click="$emit('edit-profile')">
          Edit profile
        </el-button>
      </div>

      <el-alert
        v-if="!profileCompleted"
        type="warning"
        :closable="false"
        title="Your profile is incomplete."
        description="Complete your employee profile to access all features."
        class="profile-alert"
      />

      <div class="profile-grid">
        <!-- Personal Information -->
        <el-card class="detail-card personal-card" shadow="never">
          <h3>Personal information</h3>
          <el-descriptions :column="2" border class="personal-descriptions">
            <el-descriptions-item label="First name">{{ profile.firstName || 'Not provided' }}</el-descriptions-item>
            <el-descriptions-item label="Last name">{{ profile.lastName || 'Not provided' }}</el-descriptions-item>
            <el-descriptions-item label="Email">{{ profile.email || 'Not provided' }}</el-descriptions-item>
            <el-descriptions-item label="Primary contact">{{ profile.primaryContactNumber || 'Not provided' }}</el-descriptions-item>
            <el-descriptions-item label="Country of citizenship">{{ profile.countryOfCitizenship || 'Not provided' }}</el-descriptions-item>
          </el-descriptions>
        </el-card>

        <!-- Work Profile -->
        <el-card class="detail-card summary-card" shadow="never">
          <h3>Work profile</h3>
          <el-descriptions :column="1" border>
            <el-descriptions-item label="Departments">
              <div v-if="departmentsLabel" class="tag-group">
                <el-tag v-for="dept in profile.departments" :key="dept" class="tag">
                  {{ dept }}
                </el-tag>
              </div>
              <span v-else>Not provided</span>
            </el-descriptions-item>
            <el-descriptions-item v-if="profile.departments.includes('Other')" label="Other departments">
              {{ profile.departmentsOther || 'Not provided' }}
            </el-descriptions-item>
            <el-descriptions-item label="Services">
              <div v-if="servicesLabel" class="tag-group">
                <el-tag v-for="service in profile.serviceCategories" :key="service" type="success" class="tag">
                  {{ service }}
                </el-tag>
              </div>
              <span v-else>Not provided</span>
            </el-descriptions-item>
            <el-descriptions-item v-if="profile.serviceCategories.includes('Other')" label="Other services">
              {{ profile.serviceCategoriesOther || 'Not provided' }}
            </el-descriptions-item>
            <el-descriptions-item label="Weekly capacity">{{ weeklyCapacityLabel }}</el-descriptions-item>
            <el-descriptions-item label="Rate type">{{ rateTypeLabel }}</el-descriptions-item>
            <el-descriptions-item label="Rate">{{ rateDisplay }}</el-descriptions-item>
          </el-descriptions>
        </el-card>
      </div>
    </el-card>
  </div>
</template>

<script>
const WEEKLY_CAPACITY_LABELS = Object.freeze({
  low: 'Low (1 to 5 hours/week)',
  moderate: 'Moderate (6 to 15 hours/week)',
  high: 'High (15+ hours/week)',
})

const RATE_TYPE_LABELS = Object.freeze({
  hourly: 'Hourly',
  per_project: 'Per Project/Service',
})

export default {
  name: 'EmployeeProfilePanel',
  props: {
    profile: {
      type: Object,
      default: () => ({}),
    },
    profileCompleted: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['edit-profile'],
  computed: {
    departmentsLabel() {
      return Array.isArray(this.profile.departments) && this.profile.departments.length
        ? this.profile.departments.join(', ')
        : ''
    },
    servicesLabel() {
      return Array.isArray(this.profile.serviceCategories) && this.profile.serviceCategories.length
        ? this.profile.serviceCategories.join(', ')
        : ''
    },
    weeklyCapacityLabel() {
      return WEEKLY_CAPACITY_LABELS[this.profile.weeklyCapacity] || 'Not provided'
    },
    rateTypeLabel() {
      return RATE_TYPE_LABELS[this.profile.rateType] || 'Not provided'
    },
    rateDisplay() {
      const rate = this.profile.rate ? parseFloat(this.profile.rate).toFixed(2) : null
      if (!rate) return 'Not provided'
      const suffix = this.profile.rateType === 'hourly' ? '/hour' : ''
      return `$${rate}${suffix}`
    },
  },
}
</script>

<style scoped>
.profile-panel {
  width: 100%;
}

.section-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 20px;
}

.section-head > div {
  flex: 1;
}

.eyebrow {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.section-head h2 {
  margin: 4px 0 8px;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--ink);
}

.subcopy {
  margin: 0;
  font-size: 0.95rem;
  color: var(--muted);
}

.profile-alert {
  margin-bottom: 20px;
}

.profile-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(320px, 420px));
  justify-content: space-between;
  gap: 16px;
}

.detail-card {
  background: var(--surface);
  border: 1px solid var(--border-soft);
  border-radius: 12px;
  padding: 16px;
}

.personal-card {
  grid-column: 1 / -1;
  width: 100%;
  max-width: none;
}

.summary-card {
  grid-column: 1 / -1;
  width: 100%;
  max-width: none;
}

.personal-descriptions :deep(.el-descriptions__label) {
  width: 220px;
  color: var(--ink);
  font-weight: 600;
  background: color-mix(in srgb, var(--surface) 92%, #000000 8%);
}

.personal-descriptions :deep(.el-descriptions__content) {
  color: var(--ink);
  white-space: normal;
  overflow-wrap: anywhere;
}

.detail-card h3 {
  margin: 0 0 12px;
  font-size: 1rem;
  font-weight: 600;
  color: var(--ink);
  text-transform: capitalize;
}

.tag-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0;
}

.tag {
  border-radius: 4px;
}

@media (max-width: 768px) {
  .section-head {
    flex-direction: column;
    align-items: stretch;
  }

  .profile-grid {
    grid-template-columns: 1fr;
  }

  .personal-descriptions {
    --el-descriptions-table-layout: fixed;
  }

  .personal-descriptions :deep(.el-descriptions__body table) {
    table-layout: fixed;
  }

  .personal-descriptions :deep(.el-descriptions__cell) {
    display: table-cell;
  }
}
</style>
